import {
  IOrder, IProductType,
  CATEGORIES_TYPES, PRODUCT_TYPE_STRUCTURE_TYPES, IOrderLine, ORDER_DELIVERY_METHODS, ANALYTIC_EVENT_NAME
} from '@cakeiteasy/web-utils';

import { ICakeCartLine } from '../../cart/models/cart/i-cake-cart-line';
import { getCakeCartLineTotalPrice } from '../../cart/helpers/cart-helper';
import { CART_LINE_TYPES } from '../../cart/constants/common';

import {
  IAddPaymentInfoPayload,
  IAddShippingInfoPayload,
  IAddToCartPayload,
  IUserDataPaymentInfo,
  IAuthTrackPayload, IPageInfo, IVirtualPageViewPayload, ICartViewCartLine, ICartView
} from '../interfaces';
import { ICart } from '../../cart/models/cart/i-cart';
import { ICartLine } from '../../cart/models/cart/i-cart-line';
import { IPaymentInfo } from '../../payment';
import {
  capitalizeString,
  formatPrice,
  getCartLineTrackingNameFn,
  getDiscountOrderLine,
  getProductTrackingName,
  getTotalPriceValue
} from './shared-analytic.helpers';

const {
  VIRTUAL_PAGE_VIEW,
  PAGE_VIEW,
  VIEW_ITEM,
  ADD_TO_CART,
  REMOVE_FROM_CART,
  VIEW_CART,
  BEGIN_CHECKOUT,
  ADD_SHIPPING_INFO,
  ADD_PAYMENT_INFO,
  PURCHASE,
  SIGN_UP,
  LOGIN,
  LOGOUT,
  SEARCH,
  SEARCH_NO_RESULTS
} = ANALYTIC_EVENT_NAME;

export const collectUserData = (cart: ICart, contactInfo: IPaymentInfo): IUserDataPaymentInfo => {
  const {
    deliveryOrder: { type, deliveryPhone, deliveryAddress, deliveryCity, deliveryPostNumber, deliveryUserName },
    company, bakery,
  } = cart;

  return {
    isB2B: Boolean(company),
    phoneNumber: deliveryPhone,
    userId: contactInfo && contactInfo.userId,
    email: contactInfo && contactInfo.userEmail,
    address: {
      userName: deliveryUserName,
      country: bakery.country.code,
      ...(type === ORDER_DELIVERY_METHODS.DELIVERY_TO_DOOR && {
        street: deliveryAddress,
        city: deliveryCity,
        postalCode: deliveryPostNumber,
      })
    },
  };
};

export const getAuthPayload = (payload: IAuthTrackPayload) => {
  const { userId, method, isB2B, email, phone } = payload;
  return {
    user_id: userId,
    method: method,
    customer_role: isB2B ? 'b2b' : 'b2c',
    customer_email: email,
    customer_phone: phone,
  };
};

export const getSignUpTrack = (payload: IAuthTrackPayload): [string, any] => {
  return [SIGN_UP, getAuthPayload(payload)];
};

export const getLoginTrack = (payload: IAuthTrackPayload): [string, any] => {
  return [LOGIN, getAuthPayload(payload)];
};

export const getLogoutTrack = (): [string, any] => {
  return [LOGOUT, null];
};

export const getPageTrack = (pageInfo: IPageInfo): [string, any] => {
  const { fullUrl, title, path, sessionPageNumber } = pageInfo;

  return [PAGE_VIEW, {
    page_location: fullUrl,
    page_title: title,
    page_path: path,
    session_page_number: sessionPageNumber
  }];
};

export const getVirtualPageViewTrack = (data: IVirtualPageViewPayload): [string, any] => {
  const { pageTitle, pageFullUrl, previousUrl, cityName } = data;

  return [VIRTUAL_PAGE_VIEW, {
    page_title: pageTitle,
    page_location: pageFullUrl,
    previous_url: previousUrl,
    ...(cityName && { city_page: cityName }),
  }];
};

export const getSearchTrack = (data: { searchTerm: string, pageLocation: string }): [string, any] => {
  const { searchTerm, pageLocation } = data;
  return [SEARCH, {
    search_term: searchTerm,
    page_location: pageLocation
  }];
};

export const getSearchNoResultTrack = (data: { searchTerm: string, pageLocation: string }): [string, any] => {
  const { searchTerm, pageLocation } = data;
  return [SEARCH_NO_RESULTS, {
    search_term: searchTerm,
    page_location: pageLocation
  }];
};

export const getPaymentUserTrack = (userData: IUserDataPaymentInfo) => {
  const {
    email, isB2B, phoneNumber: phone_number,
    address: { userName: user_name, country, street = '', city = '', postalCode = '', }
  } = userData;

  return {
    email,
    customer_role: isB2B ? 'b2b' : 'b2c',
    phone_number,
    address: { user_name, country, street, city, postal_code: postalCode },
  };
};

export const getViewItemTrack = (product: IProductType): [string, any] => {
  const { bakery: { country: { currency } } } = product;
  return [VIEW_ITEM, {
    ecommerce: {
      currency,
      items: [{
        item_id: product.id,
        item_name: product.name,
        item_category: product.category?.name || 'Cake',
        affiliation: product.bakery.name,
      }],
    }
  }];
};

const receiveUserDataFromPayment = (order: IOrder) => {
  const {
    deliveryOrder: { type, deliveryPhone, deliveryAddress, deliveryCity, deliveryPostNumber, deliveryUserName },
    multiOrder: { company, customer }, bakery,
  } = order;

  return {
    email: customer && customer.contactEmail,
    customer_role: Boolean(company) ? 'b2b' : 'b2c',
    phone_number: deliveryPhone,
    address: {
      user_name: deliveryUserName,
      country: bakery.country.code,
      ...(type === ORDER_DELIVERY_METHODS.DELIVERY_TO_DOOR && {
        street: deliveryAddress,
        city: deliveryCity,
        postal_code: deliveryPostNumber,
      })
    },
  };
};

export const getOrderPurchaseTrack = (order: IOrder): [string, any] => {
  const items = order.orderLines.map((line) => {
    const details = line.details;

    const categories = ((details && details.categories) || [])
      .filter((category: { type: string; name: string }) => category.type === CATEGORIES_TYPES.GLOBAL)
      .map((category: { type: string; name: string }) => category.name);

    const applyOnce = details && details.applyOnce;
    const variant = details && details.variant && details.variant.name;

    return {
      item_id: line.id,
      item_name: line.name,
      item_variant: applyOnce ? line.name : variant,
      price: formatPrice(getOrderLinePurePrice(line)),
      affiliation: order.bakery.name,
      item_category: categories && categories[0],
      quantity: line.quantity,
    };
  });

  const discountLine = getDiscountOrderLine(order.multiOrder.orderLines);

  return [PURCHASE, {
    ecommerce: {
      value: formatPrice(order.priceWithoutVat),
      currency: order.bakery.country.currency,
      transaction_id: order.multiOrder.number,
      shipping: formatPrice(order.deliveryOrder.price),
      tax: formatPrice(order.price - order.priceWithoutVat),
      coupon: discountLine && discountLine.details && discountLine.details.code,
      items,
    },
    userData: receiveUserDataFromPayment(order),
  }];
};

const getOrderLinePurePrice = (line: IOrderLine): number => {
  return line.orderLines.reduce((acc, { price }) => (acc += price, acc), line.price);
};

export const getPaymentTrack = (payload: IAddPaymentInfoPayload): [string, any] => {
  const { lines, paymentType, bakery: { name: bakeryName, country: { currency } }, userData, discount, giftCard } = payload;

  const items = lines.map(line => {
    const applyOnce = line.details && line.details.applyOnce;
    const variant = line.variant && line.variant.name;

    return {
      item_id: line.id,
      item_name: line.name,
      item_variant: applyOnce ? line.name : variant,
      affiliation: bakeryName,
      price: formatPrice(getCakeCartLineTotalPrice({ line, isPurePrice: true })),
      quantity: line.quantity,
    };
  });


  return [ADD_PAYMENT_INFO, {
    ecommerce: {
      currency,
      value: formatPrice(getTotalPriceValue(lines)),
      payment_type: capitalizeString(paymentType),
      ...(discount && { coupon: discount }),
      ...(giftCard && { gift_card: giftCard }),
      items,
    },
    userData: getPaymentUserTrack(userData),
  }];
};

export const getAddShippingInfoTrack = (payload: IAddShippingInfoPayload): [string, any] => {
  return makeShippingEvent(ADD_SHIPPING_INFO, payload);
};

export const getBeginCheckoutTrack = (payload: IAddShippingInfoPayload): [string, any] => {
  return makeShippingEvent(BEGIN_CHECKOUT, payload);
};

export const makeShippingEvent = (eventName: string, { lines, shippingMethod, bakery }: IAddShippingInfoPayload): [string, any] => {
  const { country: { currency } } = bakery;
  return [eventName, {
    ecommerce: {
      currency,
      value: formatPrice(getTotalPriceValue(lines)),
      items: lines.map((line) =>
        mapToAddCartPayload({
          line,
          bakery,
          getNameFn: getProductTrackingName,
        })),
      ...(shippingMethod && { shipping_tier: shippingMethod })
    },
  }];
};


export const getAddToCartTrack = (payload: IAddToCartPayload): [string, any] => {
  const { bakery, categoryName, subCategoryName, line, lines, upsellLines } = payload;
  const params = { bakery, categoryName, subCategoryName };
  const { country: { currency } } = bakery;
  const items = [];
  let value = 0;

  if (line) {
    value += getTotalPriceValue([line]);
    items.push(mapToAddCartPayload({ line, ...params, getNameFn: getCartLineTrackingNameFn() }));
  }

  if (lines) {
    value += getTotalPriceValue(lines);
    items.push(...lines.map(
      (l) =>
        mapToAddCartPayload({ line: l, ...params, getNameFn: getCartLineTrackingNameFn() }))
    );
  }

  if (upsellLines) {
    value += getTotalPriceValue(upsellLines);
    items.push(...upsellLines.map(
      l => mapToAddCartPayload(
        { line: l, ...params, getNameFn: getCartLineTrackingNameFn(true) }
      ))
    );
  }

  return [ADD_TO_CART, { ecommerce: { currency, value: formatPrice(value), items } }];
};

export const getViewCartTrack = (cart: ICart): [string, any] => {
  return [VIEW_CART, {
    ecommerce: mapCartToCartViewPayload(cart, cart.lines),
  }];
};

export const getRemoveFromCartTrack = (cart: ICart, lines: ICartLine[]): [string, any] => {
  const { value, ...ecommerce } = mapCartToCartViewPayload(cart, lines);
  return [REMOVE_FROM_CART, { ecommerce }];
};

export const mapCartToCartViewPayload = (cart: ICart, lines: ICartLine[]): ICartView => {
  const { bakery: { name: affiliation, country: { currency } }, price } = cart;

  return {
    currency,
    value: formatPrice(price),
    items: lines.map(line => ({ ...mapCartLineToViewCartPayload(line), affiliation })),
  };
};

const mapCartLineToViewCartPayload = (line: ICartLine): ICartViewCartLine => {
  const { product, name: item_name, price, quantity, count } = line;

  const [item_variant, item_id]: [string, number] =
    product ? [product.variant.name, product.type.id] : [null, null];

  /**Due old logic bug, when orderLine.quantity is map to orderLine.count,
   * where no reasons to do that, and in feature we should remove count field
   * quantity: quantity || count*/
  return {
    item_name, item_id, item_variant, quantity: quantity || count,
    price: formatPrice(price),
  };
};

const mapToAddCartPayload = (payload: IAddToCartPayload & { getNameFn: (data: ICakeCartLine) => string }) => {
  const { line, getNameFn, bakery: { name: bakeryName }, categoryName, subCategoryName } = payload;

  return {
    item_id: line.product ? line.product.type.id : null,
    item_name: getNameFn(line),
    price: formatPrice(getCakeCartLineTotalPrice({ line, isPurePrice: true })),
    affiliation: bakeryName,
    item_variant: line.variant && line.variant.name,
    quantity: line.count,
    ...(categoryName && { item_category: categoryName }),
    ...(subCategoryName && { item_category2: subCategoryName }),
  };
};

export * from './shared-analytic.helpers';
export * from './posthog.helpers';
